// =============================================================================
// components/KpiIncidents/IncidentMap.jsx
//
// Mapa Leaflet com markers de sites. Estrategia de performance:
//   - Canvas renderer (preferCanvas) — muito mais rapido que SVG para
//     milhares de formas.
//   - Layer imperativa em SitesLayer — zero overhead React por marker.
//   - setStyle() para destacar/restaurar markers sem re-render.
//
// Defensividade contra React 18 Strict Mode + Leaflet lifecycle:
//   - mountedRef impede operacoes apos unmount.
//   - guards em todos os useEffects que tocam refs Leaflet.
//   - cleanup ignora erros se a layer ja foi removida.
// =============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import {
	Box, Paper, Typography, IconButton, Tooltip as MuiTooltip, Chip, Stack, Button,
} from '@mui/material';
import LayersIcon          from '@mui/icons-material/Layers';
import LayersClearIcon     from '@mui/icons-material/LayersClear';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import LocationOffIcon     from '@mui/icons-material/LocationOff';
import CloseIcon           from '@mui/icons-material/Close';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import { MapContainer, TileLayer, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

// -----------------------------------------------------------------------------
// Configuracao
// -----------------------------------------------------------------------------
const DEFAULT_CENTER = [54.5, -3.5];
const DEFAULT_ZOOM   = 5;
const SELECTED_ZOOM  = 20;
const MAP_MAX_ZOOM   = 21;

const TILE_LAYERS = {
	street: {
		url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
		attribution: '&copy; OpenStreetMap',
		maxNativeZoom: 19,
		maxZoom: MAP_MAX_ZOOM,
	},
	satellite: {
		url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
		attribution: '&copy; Esri',
		maxNativeZoom: 19,
		maxZoom: MAP_MAX_ZOOM,
	},
};

// -----------------------------------------------------------------------------
// Helpers puros
// -----------------------------------------------------------------------------
function colorForSeverity(severityMap, sev) {
	if (severityMap && severityMap[sev] && severityMap[sev].colorHex) {
		return severityMap[sev].colorHex;
	}
	return '#888888';
}

function radiusForCount(count, isSelected) {
	if (isSelected) return 8;
	if (count >= 5) return 6;
	if (count >= 3) return 5;
	if (count >= 2) return 4;
	return 3;
}

function styleForSite(site, isSelected, severityMap) {
	const color = colorForSeverity(severityMap, site.worstSeverity);
	return {
		radius:      radiusForCount(site.count, isSelected),
		color:       color,
		fillColor:   color,
		fillOpacity: isSelected ? 0.9 : 0.7,
		weight:      isSelected ? 2 : 1,
	};
}

function tooltipHtmlForSite(site, severityMap) {
	const color    = colorForSeverity(severityMap, site.worstSeverity);
	const sevLabel = (severityMap && severityMap[site.worstSeverity])
		? severityMap[site.worstSeverity].label : '—';

	const name   = site.cellSiteName
		? '<div style="color:#475569">' + site.cellSiteName + '</div>' : '';
	const cities = site.cities
		? '<div style="color:#64748b;font-size:0.7rem">' + site.cities + '</div>' : '';

	return (
		'<div style="font-family:Arial;font-size:0.75rem">' +
		'<div style="font-weight:700">' + site.siteId + '</div>' +
		name + cities +
		'<div style="margin-top:4px">' +
		'<span style="color:' + color + ';font-weight:600">' + sevLabel + '</span>' +
		' · ' + site.count + ' incident' + (site.count > 1 ? 'es' : '') +
		'</div></div>'
	);
}

function clusterIconCreate(cluster) {
	const count = cluster.getChildCount();
	let color;
	if (count >= 100)      color = '#7f1d1d';
	else if (count >= 25)  color = '#b91c1c';
	else if (count >= 10)  color = '#dc2626';
	else if (count >= 5)   color = '#ea580c';
	else                   color = '#0891b2';

	const size = count < 10 ? 28 : count < 100 ? 34 : 42;
	const html =
		'<div style="' +
		'width:' + size + 'px;height:' + size + 'px;' +
		'border-radius:50%;background:' + color + ';' +
		'border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);' +
		'display:flex;align-items:center;justify-content:center;' +
		'color:#fff;font-family:Arial;font-weight:700;' +
		'font-size:' + (count < 100 ? 12 : 11) + 'px;opacity:0.92;' +
		'">' + count + '</div>';

	return L.divIcon({
		html: html,
		className: 'tkad-cluster-icon',
		iconSize: L.point(size, size),
	});
}

// -----------------------------------------------------------------------------
// MapController — setView programatico (clique na tabela)
// -----------------------------------------------------------------------------
function MapController({ flyTo }) {
	const map = useMap();
	useEffect(function () {
		if (!map || !flyTo || flyTo.lat == null || flyTo.lng == null) return;
		try {
			map.setView(
				[flyTo.lat, flyTo.lng],
				flyTo.zoom || SELECTED_ZOOM,
				{ animate: false }
			);
		} catch (e) {}
	}, [flyTo, map]);
	return null;
}

// -----------------------------------------------------------------------------
// MapResizeWatcher
// -----------------------------------------------------------------------------
function MapResizeWatcher() {
	const map = useMap();
	useEffect(function () {
		if (!map) return;
		const container = map.getContainer();
		if (!container || !container.parentElement) return;
		if (typeof ResizeObserver === 'undefined') return;

		const parent = container.parentElement;
		let lastW = parent.clientWidth;
		let lastH = parent.clientHeight;
		let timer = null;
		let cancelled = false;

		const observer = new ResizeObserver(function () {
			if (cancelled) return;
			const w = parent.clientWidth;
			const h = parent.clientHeight;
			if (Math.abs(w - lastW) < 2 && Math.abs(h - lastH) < 2) return;
			lastW = w; lastH = h;
			if (timer != null) clearTimeout(timer);
			timer = setTimeout(function () {
				if (cancelled) return;
				try { map.invalidateSize(false); } catch (e) {}
				timer = null;
			}, 100);
		});
		observer.observe(parent);

		const initialTimer = setTimeout(function () {
			if (cancelled) return;
			try { map.invalidateSize(false); } catch (e) {}
		}, 200);

		return function () {
			cancelled = true;
			observer.disconnect();
			if (timer != null) clearTimeout(timer);
			clearTimeout(initialTimer);
		};
	}, [map]);
	return null;
}

// -----------------------------------------------------------------------------
// SitesLayer — gere imperativamente o cluster e markers
// -----------------------------------------------------------------------------
function SitesLayer({ sites, severityMap, selectedSiteId, onSiteClick }) {
	const map = useMap();

	const clusterRef     = useRef(null);
	const rendererRef    = useRef(null);
	const markersRef     = useRef(new Map());
	const mountedRef     = useRef(false);
	const onClickRef     = useRef(onSiteClick);
	const severityMapRef = useRef(severityMap);

	// Refs vivas para props que mudam frequentemente sem refazer a layer
	useEffect(function () { onClickRef.current = onSiteClick; });
	useEffect(function () { severityMapRef.current = severityMap; });

	// ---- mount/unmount: cria cluster e renderer uma vez ----------------------
	useEffect(function () {
		if (!map) return;

		const renderer = L.canvas({ padding: 0.5 });
		const cluster  = L.markerClusterGroup({
			chunkedLoading:             true,
			chunkInterval:              100,
			chunkDelay:                 30,
			maxClusterRadius:           50,
			spiderfyOnMaxZoom:          false,
			showCoverageOnHover:        false,
			animate:                    false,
			animateAddingMarkers:       false,
			removeOutsideVisibleBounds: true,
			disableClusteringAtZoom:    15,
			iconCreateFunction:         clusterIconCreate,
		});

		rendererRef.current = renderer;
		clusterRef.current  = cluster;
		mountedRef.current  = true;

		try { map.addLayer(cluster); } catch (e) {}

		return function () {
			mountedRef.current = false;
			try {
				if (cluster._map) map.removeLayer(cluster);
			} catch (e) {}
			markersRef.current.clear();
			if (clusterRef.current === cluster)  clusterRef.current = null;
			if (rendererRef.current === renderer) rendererRef.current = null;
		};
	}, [map]);

	// ---- popular markers quando sites muda -----------------------------------
	useEffect(function () {
		const cluster  = clusterRef.current;
		const renderer = rendererRef.current;
		if (!mountedRef.current || !cluster || !renderer) return;
		if (!cluster._map) return; // ainda nao attached ao map

		try { cluster.clearLayers(); } catch (e) {}
		markersRef.current.clear();

		if (!sites || sites.length === 0) return;

		const layers = [];
		for (let i = 0; i < sites.length; i++) {
			const site = sites[i];
			const isSelected = site.siteId === selectedSiteId;
			const marker = L.circleMarker(
				[site.latitude, site.longitude],
				Object.assign(
					{ renderer: renderer },
					styleForSite(site, isSelected, severityMapRef.current)
				)
			);
			marker.bindTooltip(
				tooltipHtmlForSite(site, severityMapRef.current),
				{ direction: 'top', offset: [0, -6] }
			);
			marker.on('click', function () {
				if (!mountedRef.current) return;
				const cb = onClickRef.current;
				if (cb) cb(site);
			});
			markersRef.current.set(site.key, marker);
			layers.push(marker);
		}
		try { cluster.addLayers(layers); } catch (e) {}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sites]);

	// ---- destacar marker seleccionado (sem re-render React) ------------------
	const prevSelectedRef = useRef(null);
	useEffect(function () {
		const cluster = clusterRef.current;
		if (!mountedRef.current || !cluster || !cluster._map) return;

		const markers = markersRef.current;
		const prev = prevSelectedRef.current;

		if (prev) {
			const prevSite = sites.find(function (s) { return s.siteId === prev; });
			if (prevSite) {
				const prevMarker = markers.get(prevSite.key);
				if (prevMarker) {
					try {
						prevMarker.setStyle(styleForSite(prevSite, false, severityMapRef.current));
					} catch (e) {}
				}
			}
		}

		if (selectedSiteId) {
			const newSite = sites.find(function (s) { return s.siteId === selectedSiteId; });
			if (newSite) {
				const newMarker = markers.get(newSite.key);
				if (newMarker) {
					try {
						newMarker.setStyle(styleForSite(newSite, true, severityMapRef.current));
						newMarker.bringToFront();
					} catch (e) {}
				}
			}
		}

		prevSelectedRef.current = selectedSiteId;
	}, [selectedSiteId, sites]);

	return null;
}

// =============================================================================
// IncidentMap — componente publico
// =============================================================================
export default function IncidentMap({
	items,
	siteMap,
	severityMap,
	selectedSiteId,
	selectedIncident,      // NOVO: incident clicado na tabela (para header)
	flyToCoords,
	onSiteClick,
	onClearSelection,
}) {
	const [tileMode, setTileMode] = useState('street');
	const mapRef = useRef(null);

	// -------------------------------------------------------------------------
	// Agregar incidents por site
	// -------------------------------------------------------------------------
	const sitesWithIncidents = useMemo(function () {
		const result = new Map();
		(items || []).forEach(function (inc) {
			const key = inc.vendor + '|' + inc.siteId;
			const site = siteMap.get(key);
			if (!site) return;

			let entry = result.get(key);
			if (!entry) {
				entry = {
					key:           key,
					vendor:        site.vendor,
					siteId:        site.siteId,
					cellSiteName:  site.cellSiteName,
					cities:        site.cities,
					latitude:      site.latitude,
					longitude:     site.longitude,
					count:         0,
					worstSeverity: 99,
				};
				result.set(key, entry);
			}
			entry.count += 1;
			if (inc.severity != null && inc.severity < entry.worstSeverity) {
				entry.worstSeverity = inc.severity;
			}
		});
		return Array.from(result.values());
	}, [items, siteMap]);

	const incidentsWithoutCoords = useMemo(function () {
		if (!items || !siteMap) return 0;
		let n = 0;
		for (let i = 0; i < items.length; i++) {
			const inc = items[i];
			if (!siteMap.has(inc.vendor + '|' + inc.siteId)) n += 1;
		}
		return n;
	}, [items, siteMap]);

	// -------------------------------------------------------------------------
	// Click no marker
	// -------------------------------------------------------------------------
	const handleMarkerClick = function (site) {
		const isCurrentlySelected = site.siteId === selectedSiteId;
		if (mapRef.current && !isCurrentlySelected) {
			try {
				mapRef.current.setView(
					[site.latitude, site.longitude],
					SELECTED_ZOOM,
					{ animate: false }
				);
			} catch (e) {}
		}
		onSiteClick(site);
	};

	// -------------------------------------------------------------------------
	// Estado focado: marker OU incident da tabela
	// -------------------------------------------------------------------------
	const isFocused = !!(selectedSiteId || flyToCoords);

	useEffect(function () {
		setTileMode(isFocused ? 'satellite' : 'street');
	}, [isFocused]);

	const prevFocusRef = useRef(false);
	useEffect(function () {
		const wasFocused = prevFocusRef.current;
		if (wasFocused && !isFocused && mapRef.current) {
			try {
				mapRef.current.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
			} catch (e) {}
		}
		prevFocusRef.current = isFocused;
	}, [isFocused]);

	// -------------------------------------------------------------------------
	// Toolbar handlers
	// -------------------------------------------------------------------------
	const handleResetView = function () {
		if (onClearSelection) onClearSelection(); // limpa estado, useEffect trata da view
	};

	const tile = TILE_LAYERS[tileMode];
	const selectedSiteFromMap = sitesWithIncidents.find(function (s) {
		return s.siteId === selectedSiteId;
	});

	// -------------------------------------------------------------------------
	// Conteudo do header — contextual conforme o foco
	// -------------------------------------------------------------------------
	const headerContent = (function () {
		if (selectedIncident) {
			return (
				<>
					<Typography variant="caption" fontWeight={700} sx={{
						textTransform: 'uppercase', letterSpacing: 0.4, color: '#1e40af',
					}}>
						{selectedIncident.siteId}
					</Typography>
					<Typography variant="caption" sx={{ color: '#64748b' }}>
						· {selectedIncident.cellId}
					</Typography>
					{selectedIncident.kpiName && (
						<Typography variant="caption" sx={{
							color: '#475569', fontWeight: 600,
						}}>
							· {selectedIncident.kpiName}
						</Typography>
					)}
				</>
			);
		}
		if (selectedSiteFromMap) {
			return (
				<>
					<Typography variant="caption" fontWeight={700} sx={{
						textTransform: 'uppercase', letterSpacing: 0.4, color: '#1e40af',
					}}>
						{selectedSiteFromMap.siteId}
					</Typography>
					{selectedSiteFromMap.cellSiteName && (
						<Typography variant="caption" sx={{ color: '#64748b' }}>
							· {selectedSiteFromMap.cellSiteName}
						</Typography>
					)}
					<Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
						· {selectedSiteFromMap.count} incident{selectedSiteFromMap.count > 1 ? 'es' : ''}
					</Typography>
				</>
			);
		}
		return (
			<>
				<Typography variant="caption" fontWeight={700} sx={{
					textTransform: 'uppercase', letterSpacing: 0.4, color: '#475569',
				}}>
					Mapa
				</Typography>
				<Typography variant="caption" color="text.secondary">
					· {sitesWithIncidents.length} sites
				</Typography>
			</>
		);
	})();

	// -------------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------------
	return (
		<Paper
			variant="outlined"
			sx={{
				height: '100%',
				display: 'flex', flexDirection: 'column',
				overflow: 'hidden', position: 'relative',
			}}
		>
			{/* Header — contextual */}
			<Box sx={{
				px: 1.5, py: 0.75,
				bgcolor: isFocused ? '#eff6ff' : '#fafbfc',
				borderBottom: '1px solid #e5e7eb',
				display: 'flex', alignItems: 'center', gap: 1,
				transition: 'background-color 0.15s',
			}}>
				{headerContent}
				<Box sx={{ flex: 1 }} />
				{isFocused && (
					<Chip
						size="small"
						label="Limpar"
						onDelete={onClearSelection}
						deleteIcon={<CloseIcon />}
						sx={{
							height: 22, fontSize: '0.7rem', fontWeight: 600,
							bgcolor: '#1e40af', color: 'white',
							'& .MuiChip-deleteIcon': {
								color: 'rgba(255,255,255,0.85)',
								'&:hover': { color: 'white' },
							},
						}}
					/>
				)}
			</Box>

			{/* Mapa */}
			<Box sx={{ flex: 1, position: 'relative', minHeight: 400 }}>
				<MapContainer
					center={DEFAULT_CENTER}
					zoom={DEFAULT_ZOOM}
					maxZoom={MAP_MAX_ZOOM}
					preferCanvas={true}
					style={{ height: '100%', width: '100%' }}
					zoomControl={false}
					ref={mapRef}
				>
					<TileLayer
						url={tile.url}
						attribution={tile.attribution}
						maxNativeZoom={tile.maxNativeZoom}
						maxZoom={tile.maxZoom}
					/>
					<ZoomControl position="bottomright" />

					<MapController flyTo={flyToCoords} />
					<MapResizeWatcher />

					<SitesLayer
						sites={sitesWithIncidents}
						severityMap={severityMap}
						selectedSiteId={selectedSiteId}
						onSiteClick={handleMarkerClick}
					/>
				</MapContainer>

				{/* "Voltar a vista geral" — visivel para QUALQUER foco */}
				{isFocused && (
					<Box sx={{
						position: 'absolute', top: 12, left: '50%',
						transform: 'translateX(-50%)', zIndex: 1000,
					}}>
						<Button
							variant="contained"
							size="small"
							startIcon={<ArrowBackIcon />}
							onClick={onClearSelection}
							sx={{
								bgcolor: 'white', color: '#1e293b',
								boxShadow: 2, textTransform: 'none',
								fontWeight: 600, fontSize: '0.78rem',
								px: 1.5, py: 0.5,
								'&:hover': { bgcolor: '#f1f5f9', boxShadow: 3 },
							}}
						>
							Voltar à vista geral
						</Button>
					</Box>
				)}

				{/* Toolbar */}
				<Stack
					direction="column"
					spacing={0.5}
					sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}
				>
					<MuiTooltip
						title={tileMode === 'street' ? 'Mudar para satelite' : 'Mudar para street'}
						placement="left"
					>
						<IconButton
							size="small"
							onClick={function () {
								setTileMode(function (m) { return m === 'street' ? 'satellite' : 'street'; });
							}}
							sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#f8fafc' } }}
						>
							{tileMode === 'street'
								? <LayersIcon fontSize="small" />
								: <LayersClearIcon fontSize="small" />}
						</IconButton>
					</MuiTooltip>

					<MuiTooltip title="Reset view" placement="left">
						<IconButton
							size="small"
							onClick={handleResetView}
							sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#f8fafc' } }}
						>
							<CenterFocusStrongIcon fontSize="small" />
						</IconButton>
					</MuiTooltip>
				</Stack>

				{incidentsWithoutCoords > 0 && (
					<Box sx={{
						position: 'absolute', bottom: 8, left: 8, zIndex: 1000,
						bgcolor: 'rgba(255,255,255,0.95)',
						border: '1px solid #e5e7eb', borderRadius: 1,
						px: 1, py: 0.5,
						display: 'flex', alignItems: 'center', gap: 0.5,
						boxShadow: 1,
					}}>
						<LocationOffIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
						<Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#475569' }}>
							{incidentsWithoutCoords} incident{incidentsWithoutCoords > 1 ? 'es' : ''} sem localizacao
						</Typography>
					</Box>
				)}
			</Box>
		</Paper>
	);
}